import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import useNotifications from "@/hooks/useNotifications";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Subscription from "@/pages/Subscription";
import SubscriptionCallback from "@/pages/SubscriptionCallback";
import Settings from "@/pages/Settings";
import NotFound from "@/pages/NotFound";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Responsible from "@/pages/legal/Responsible";
import Results from "@/pages/Results";
import Live from "@/pages/Live";
import Admin from "@/pages/Admin";
import Fantasy from "@/pages/Fantasy";
import FplCaptainOfTheWeek from "@/pages/FplCaptainOfTheWeek";

import "@/App.css";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pulse-sharp">
                    LOADING...
                </p>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function GlobalNotifications() {
    const { user } = useAuth();
    useNotifications(!!user);
    return null;
}

function App() {
    return (
        <ErrorBoundary>
            <HelmetProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <Toaster richColors position="top-right" theme="dark" />
                        <GlobalNotifications />
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/verify" element={<VerifyEmail />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/responsible-play" element={<Responsible />} />
                            <Route path="/results" element={<Results />} />
                            <Route path="/live" element={<Live />} />
                            <Route path="/fpl/captain" element={<FplCaptainOfTheWeek />} />
                            <Route path="/fpl/:gw/captain" element={<FplCaptainOfTheWeek />} />
                            <Route
                                path="/admin"
                                element={
                                    <Protected>
                                        <Admin />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <Protected>
                                        <Dashboard />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/predictions"
                                element={<Navigate to="/dashboard" replace />}
                            />
                            <Route
                                path="/subscription"
                                element={
                                    <Protected>
                                        <Subscription />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/subscription/callback"
                                element={
                                    <Protected>
                                        <SubscriptionCallback />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/settings"
                                element={
                                    <Protected>
                                        <Settings />
                                    </Protected>
                                }
                            />
                            <Route
                                path="/fantasy"
                                element={
                                    <Protected>
                                        <Fantasy />
                                    </Protected>
                                }
                            />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </AuthProvider>
                </BrowserRouter>
            </HelmetProvider>
        </ErrorBoundary>
    );
}

export default App;
